// ========================================
// 📄 src/components/leads/LeadExport.jsx
// ========================================
import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, File } from "lucide-react";
import { exportService } from "../../services/exportService";

const LeadExport = ({ leads, onExport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");

  const handleExport = async () => {
    if (!leads || leads.length === 0) {
      alert("Aucun prospect à exporter");
      return;
    }

    setIsExporting(true);
    try {
      await exportService.exportLeads(leads, exportFormat);
      if (onExport) {
        onExport(exportFormat);
      }
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      alert("Erreur lors de l'export des données");
    } finally {
      setIsExporting(false);
    }
  };

  const exportOptions = [
    {
      value: "pdf",
      label: "PDF",
      icon: FileText,
      description: "Document PDF formaté",
    },
    {
      value: "excel",
      label: "Excel",
      icon: FileSpreadsheet,
      description: "Fichier Excel (.xlsx)",
    },
    {
      value: "csv",
      label: "CSV",
      icon: File,
      description: "Fichier CSV simple",
    },
  ];

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <Download className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800">
            Exporter les prospects
          </h3>
          <p className="text-sm text-gray-600">
            {leads?.length || 0} prospect(s) disponible(s)
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Format d'export */}
        <div>
          <label className="label">Format d'export</label>
          <div className="grid grid-cols-1 gap-3">
            {exportOptions.map((option) => {
              const Icon = option.icon;
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    exportFormat === option.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value={option.value}
                    checked={exportFormat === option.value}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="text-blue-600"
                  />
                  <Icon className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">
                      {option.label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Informations sur l'export */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-2">
            Données incluses dans l'export :
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Informations personnelles (nom, prénom, contact)</li>
            <li>• Informations entreprise (nom, fonction)</li>
            <li>• Statut et source du prospect</li>
            <li>• Date de prospection</li>
            <li>• Produit d'intérêt</li>
            {exportFormat === "pdf" && <li>• Statistiques et graphiques</li>}
          </ul>
        </div>

        {/* Bouton d'export */}
        <button
          onClick={handleExport}
          disabled={isExporting || !leads || leads.length === 0}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isExporting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Export en cours...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Exporter ({leads?.length || 0} prospects)
            </>
          )}
        </button>

        {leads && leads.length === 0 && (
          <p className="text-sm text-gray-500 text-center">
            Aucun prospect à exporter
          </p>
        )}
      </div>
    </div>
  );
};

export default LeadExport;
